import {
  APP_BAR_ISSUE_FEEDBACK_IN_MENU,
  APP_BAR_ISSUE_FEEDBACK_BUTTON,
  SUBMISSION_FILE_UPLOAD_DRAWER,
} from "../../flags";
import { FeedbackDialog } from "../feedback/FeedbackDialog";
import AppMenu from "./AppMenu";
import Disabled from "./Disabled";
import Logo from "./Logo";
import SubmissionFileUploadDrawer from "./SubmissionFileUploadDrawer";
import Visible from "./Visible";
import WorkspaceBadge from "./WorkspaceBadge";
import {
  AddCircleRounded as AddIcon,
  SearchRounded as SearchIcon,
  AccountCircleRounded as AccountIcon,
  SupportAgentRounded as FeedbackIcon,
  Logout as SignOutIcon,
  FolderOpen as FolderIcon,
  ArticleRounded as TemplateIcon,
} from "@mui/icons-material";
import {
  AppBar as MUIAppBar,
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { extractNameFromEmail, stringToColor } from "common/strings";
import { useCallback, useState } from "react";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import * as persistenceActions from "store/actions/persistence/persistenceActions";

const UserAvatar = ({ email }) => {
  const meta = extractNameFromEmail(email);

  return (
    <Stack flexDirection={"row"} alignItems={"center"} gap={1}>
      <Avatar sx={{ bgcolor: stringToColor(meta.name) }}>
        {meta.initials}
      </Avatar>
      <Stack>
        <Typography variant={"caption"}>{"Signed in as"}</Typography>
        <Typography>{email}</Typography>
      </Stack>
    </Stack>
  );
};

const UNKNOWN_VERSION = "Unknown";
const APP_VERSION = process.env.REACT_APP_VERSION || UNKNOWN_VERSION;

const AccountMenu = ({
  anchorEl,
  closeMenu,
  email,
  onSignOut,
  onFeedback,
  onTemplateRequests,
}) => (
  <Menu
    anchorEl={anchorEl}
    open={anchorEl != null}
    onClose={(_, reason) => {
      if (reason === "backdropClick") closeMenu();
    }}
  >
    <MenuItem disabled sx={{ opacity: "1 !important", minWidth: "280px" }}>
      <UserAvatar email={email} />
    </MenuItem>

    <Divider />

    <MenuItem onClick={onTemplateRequests}>
      <ListItemText primary={"Template Requests"} />
      <ListItemIcon>
        <TemplateIcon />
      </ListItemIcon>
    </MenuItem>
    <Visible byTag={APP_BAR_ISSUE_FEEDBACK_IN_MENU} defaultVisible={false}>
      <MenuItem onClick={onFeedback}>
        <ListItemText primary={"Feedback"} />
        <ListItemIcon>
          <FeedbackIcon />
        </ListItemIcon>
      </MenuItem>
    </Visible>
    <MenuItem onClick={onSignOut}>
      <ListItemText primary={"Sign Out"} />
      <ListItemIcon>
        <SignOutIcon />
      </ListItemIcon>
    </MenuItem>

    {APP_VERSION !== UNKNOWN_VERSION && [
      <Divider key={"version-divider"} />,
      <MenuItem key={"version"} disabled sx={{ opacity: "1 !important" }}>
        <Typography variant={"caption"} color={"text.secondary"}>
          {`Version ${APP_VERSION}`}
        </Typography>
      </MenuItem>,
    ]}
  </Menu>
);

const AppBar = (props) => {
  const [title, setTitle] = useState("Submissions");

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const minHeight = "3rem";

  const navigate = useNavigate();
  const createNewSubmission = useCallback(() => {
    props.newSubmission({ navigate });
  }, [navigate, props]);

  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
  const onFeedback = (_) => {
    closeMenu();
    setFeedbackDialogOpen(true);
  };

  const onTemplateRequests = (_) => {
    closeMenu();
    navigate("/template-requests");
  };

  return (
    <MUIAppBar className={"app-bar"} position={"sticky"} elevation={0}>
      <Toolbar
        disableGutters={true}
        sx={{ minHeight: minHeight + " !important", padding: "0 0.5rem" }}
      >
        <AppMenu onChange={(label) => setTitle(label)} />
        <Logo height={20} />
        <Stack direction={"row"} alignItems={"center"} gap={1} ml={"0.5rem"}>
          <Typography variant={"subtitle1"} sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          <WorkspaceBadge />
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        <Stack
          flexDirection={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={0.5}
        >
          <Tooltip title={"New Submission"}>
            <span>
              <Disabled ifReadOnly>
                <IconButton
                  onClick={() => createNewSubmission()}
                  color={"primary"}
                  aria-label={"New Submission"}
                >
                  <AddIcon />
                </IconButton>
              </Disabled>
            </span>
          </Tooltip>
          <Visible byTag={SUBMISSION_FILE_UPLOAD_DRAWER} defaultVisible={false}>
            <Tooltip title={"Upload Files"}>
              <span>
                <Disabled ifReadOnly>
                  <IconButton
                    onClick={() => setUploadDrawerOpen(true)}
                    sx={{ padding: "0.4375rem" }}
                    aria-label={"Upload Files"}
                  >
                    <FolderIcon sx={{ fontSize: "1.625rem" }} />
                  </IconButton>
                </Disabled>
              </span>
            </Tooltip>
          </Visible>
          <Link to={"/submissions"}>
            <Tooltip title={"Search"}>
              <IconButton sx={{ padding: "0.4375rem" }}>
                <SearchIcon sx={{ fontSize: "1.625rem" }} />
              </IconButton>
            </Tooltip>
          </Link>

          <Visible byTag={APP_BAR_ISSUE_FEEDBACK_BUTTON} defaultVisible={false}>
            <Tooltip title={"Feedback"}>
              <IconButton sx={{ padding: "0.4375rem" }} onClick={onFeedback}>
                <FeedbackIcon sx={{ fontSize: "1.625rem" }} />
              </IconButton>
            </Tooltip>
          </Visible>

          <IconButton
            sx={{ padding: "0.25rem" }}
            onClick={openMenu}
            aria-label={"Account"}
          >
            <AccountIcon sx={{ fontSize: "2rem" }} />
          </IconButton>
        </Stack>
      </Toolbar>

      <AccountMenu
        anchorEl={anchorEl}
        closeMenu={closeMenu}
        email={props.attributes?.email || "Unknown"}
        onSignOut={(e) => {
          e.preventDefault();
          navigate("/logout");
        }}
        onFeedback={onFeedback}
        onTemplateRequests={onTemplateRequests}
      />

      <FeedbackDialog
        open={feedbackDialogOpen}
        close={() => setFeedbackDialogOpen(false)}
      />

      <SubmissionFileUploadDrawer
        open={uploadDrawerOpen}
        onClose={() => setUploadDrawerOpen(false)}
      />
    </MUIAppBar>
  );
};

const mapDispatchToProps = {
  newSubmission: persistenceActions.newSubmission,
};

const mapStateToProps = (state) => {
  return {
    attributes: state.user?.attributes || {},
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AppBar);
